/**
 * The Adjust rail beside a deal view: the model's primary inputs with their
 * current value and where it comes from, editable as property overrides
 * (`PUT .../overrides` with the overrides read's revision and source identity),
 * and the assumption and target inputs as read-only lists.
 *
 * Stock models leave the works budgets and the GDV to the common personal
 * controls: the GDV opens the personal GDV editor here, the works budgets
 * point at the refurbishment panel. Apply saves every staged value at once;
 * Reset clears this property's overrides. The save is synchronous on the
 * server (it recalculates inline), and the hook's invalidation refreshes the
 * model list (a forked profile id) and the result. The inputs stay editable
 * while values are re-read or saved; only Apply and Reset wait.
 */
import { useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { useIsMutating, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import type { DealModelLayout, DealModelLayoutInput } from '../../../api/dealModelLayout'
import { dealModelKeys, useSaveDealsV2PropertyOverrides, type DealsV2PropertyOverrides } from '../../../api/dealModels'
import { isFinancialSyncBusy, type DealsV2PropertyModelChoice } from '../../../api/financial'
import { ApiError } from '../../../api/http'
import { Alert, Button, Field, NumberInput, Select, Switch, TextInput } from '../../../ui/kit'
import { isPersonalControlOverride } from '../../deal-models/propertyOverrides'
import { settingsOptionLabel } from '../../settings/settingsFieldPresentation'
import { SourceChip } from './DealView'
import { draftToOverride, editKind, formatInputValue, inputsByTier, resolveInput, tokenToDraft, type DraftValue, type InputContext } from './dealLayout'
import { PersonalGdvValue, type GdvEditingContext } from './PersonalGdv'

type OverridesQuery = Pick<UseQueryResult<DealsV2PropertyOverrides, Error>, 'data' | 'error' | 'isError' | 'isPending' | 'isFetching' | 'refetch'>

const WORKS_PREFIX = 'diligence.refurbishment.'

function showRefurbishmentPanel() {
  const panel = document.getElementById('fin-refurb')
  const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  panel?.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' })
  panel?.focus({ preventScroll: true })
}

/** The override keys the model's current recipe accepts for this property (stock personal controls excluded); null without a layout. */
export function declaredOverrideKeys(layout: DealModelLayout | undefined, logicalModelId: string): Set<string> | null {
  if (!layout) return null
  return new Set(layout.inputs.flatMap(input => input.propertyOverrideKey && !isPersonalControlOverride(logicalModelId, input) ? [input.propertyOverrideKey] : []))
}

/** A save failure's title from what the server refused; nothing is retried automatically. */
export function saveFailureTitle(error: unknown, fallback: string): string {
  if (isFinancialSyncBusy(error)) return 'Calculation is busy'
  if (!(error instanceof ApiError) || error.status !== 409) return fallback
  const message = error.message.toLowerCase()
  if (message.includes('override revision')) return 'Your values changed elsewhere'
  if (message.includes('recipe changed')) return 'This model changed'
  if (message.includes('settings changed') || message.includes('model settings change')) return 'Your settings changed'
  return 'Your values or settings changed'
}

function ReadOnlyList({ inputs, context }: { inputs: DealModelLayoutInput[]; context: InputContext }) {
  return <dl className="fin-adj-list">{inputs.map(input => {
    const resolved = resolveInput(input, context)
    return <div className="fin-row" key={input.key}><dt>{input.label}</dt><dd>{formatInputValue(input, resolved.value)} <SourceChip source={resolved.source} /></dd></div>
  })}</dl>
}

export function AdjustRail({ model, layout, propertyId, profileId, overrides, context, gdvEditor, modelDataLink, hasReturnTargets = false }: {
  model: DealsV2PropertyModelChoice
  layout: DealModelLayout
  propertyId: string
  profileId: string
  overrides: OverridesQuery
  context: InputContext
  gdvEditor: GdvEditingContext
  modelDataLink?: ReactNode
  hasReturnTargets?: boolean
}) {
  const baseId = useId()
  const client = useQueryClient()
  const headingRef = useRef<HTMLSpanElement>(null)
  const save = useSaveDealsV2PropertyOverrides(propertyId)
  // "Calculate now" saves through the same key: neither starts while the other is in flight.
  const savesInFlight = useIsMutating({ mutationKey: dealModelKeys.savePropertyOverrides(propertyId) })
  /** Staged edits by input key; `removed` stages clearing a saved override by its override key. */
  const [drafts, setDrafts] = useState<Record<string, DraftValue>>({})
  const [removed, setRemoved] = useState<Record<string, true>>({})
  const [notice, setNotice] = useState<string | null>(null)
  const groups = useMemo(() => {
    const targets = hasReturnTargets ? layout.returnTargets : null
    const dedicated = new Set(targets ? [targets.modeInputKey, ...[targets.margin, targets.profit].flatMap(metric => metric ? [metric.inputKey] : [])] : [])
    // Keep the declared override keys when saving unrelated assumptions; only
    // their duplicate controls move to the dedicated return-target section.
    return inputsByTier({ ...layout, inputs: layout.inputs.filter(input => !dedicated.has(input.key)) })
  }, [layout, hasReturnTargets])
  const saved = overrides.data?.profileId === profileId && overrides.data.propertyId === propertyId ? overrides.data : undefined
  // Inputs stay enabled (and keep focus) through re-reads and saves; Apply and Reset need the current revision.
  const editableNow = Boolean(saved)
  const ready = editableNow && !overrides.isFetching && savesInFlight === 0

  const editable = (input: DealModelLayoutInput) => input.editable && input.control !== 'readOnly' && Boolean(input.propertyOverrideKey) && !isPersonalControlOverride(model.logicalModelId, input)
  // What a staged removal falls back to: the same context without that override.
  const fallbackContext = useMemo<InputContext>(() => {
    if (!context.overrides || !Object.keys(removed).length) return context
    const remaining = Object.fromEntries(Object.entries(context.overrides).filter(([key]) => !removed[key]))
    return { ...context, overrides: remaining }
  }, [context, removed])

  const staged = layout.inputs.flatMap(input => input.key in drafts && input.propertyOverrideKey ? [{ input, key: input.propertyOverrideKey, outcome: draftToOverride(input, drafts[input.key] ?? null) }] : [])
  const invalid = staged.some(entry => !entry.outcome.ok)
  const hasSaved = (key: string) => Boolean(saved && Object.hasOwn(saved.values, key))
  // An emptied nullable field changes something only when it removes a saved override.
  const changes = staged.filter(entry => !(entry.outcome.ok && 'clear' in entry.outcome) || hasSaved(entry.key))
  const pending = changes.length + Object.keys(removed).length
  const savedCount = saved ? Object.keys(saved.values).length : 0

  const discard = () => { setDrafts({}); setRemoved({}) }
  const write = (values: Record<string, unknown>, sent?: { drafts: Record<string, DraftValue>; removed: Record<string, true> }) => {
    if (!saved) return
    setNotice(null)
    save.mutate({ profileId, propertyId, expectedRevision: saved.revision, expectedSourceSha256: saved.sourceSha256, values }, {
      onSuccess: () => {
        // Keep edits made while the save was in flight; drop only what was sent.
        if (sent) {
          setDrafts(current => Object.fromEntries(Object.entries(current).filter(([key, value]) => !(key in sent.drafts) || sent.drafts[key] !== value)))
          setRemoved(current => Object.fromEntries(Object.entries(current).filter(([key]) => !sent.removed[key])) as Record<string, true>)
        } else discard()
        setNotice('Saved. Results recalculated.')
        headingRef.current?.focus()
      },
    })
  }
  const apply = () => {
    if (!saved || invalid || !pending) return
    // Only keys the current recipe declares: a removed or renamed key would be refused.
    const declared = declaredOverrideKeys(layout, model.logicalModelId)
    const values: Record<string, unknown> = Object.fromEntries(Object.entries(saved.values).filter(([key]) => !declared || declared.has(key)))
    for (const key of Object.keys(removed)) delete values[key]
    for (const entry of staged) {
      if (!entry.outcome.ok) continue
      if ('clear' in entry.outcome) delete values[entry.key]
      else values[entry.key] = entry.outcome.value
    }
    write(values, { drafts, removed })
  }
  const reset = () => { if (savedCount) write({}); else discard() }
  const reload = async () => {
    save.reset()
    discard()
    // The model list carries the profile id and layout; the rail follows it to the current profile.
    await client.invalidateQueries({ queryKey: ['dealsV2'] })
  }
  const stage = (input: DealModelLayoutInput, value: DraftValue) => {
    save.reset()
    setNotice(null)
    setDrafts(current => ({ ...current, [input.key]: value }))
    if (input.propertyOverrideKey) setRemoved(current => { if (!current[input.propertyOverrideKey!]) return current; const next = { ...current }; delete next[input.propertyOverrideKey!]; return next })
  }
  const clearOverride = (input: DealModelLayoutInput) => {
    save.reset()
    setNotice(null)
    setDrafts(current => { const next = { ...current }; delete next[input.key]; return next })
    setRemoved(current => ({ ...current, [input.propertyOverrideKey!]: true }))
  }

  const field = (input: DealModelLayoutInput) => {
    const id = `${baseId}-${input.key}`
    const shownContext = input.propertyOverrideKey && removed[input.propertyOverrideKey] ? fallbackContext : context
    const resolved = resolveInput(input, shownContext)
    const help = input.helpText ?? input.description
    const label = <>{input.label} <SourceChip source={resolved.source} /></>
    const stockGdv = input.factPath === 'card.valuation.gdvPence' && isPersonalControlOverride(model.logicalModelId, input)
    if (stockGdv) {
      return <div className="fin-adj-field" key={input.key}>
        <div className="fin-adj-h"><span className="fld-label">{label}</span><PersonalGdvValue {...gdvEditor} /></div>
        <p className="fin-faint">Your GDV applies to every model for this property.</p>
      </div>
    }
    if (!editable(input) || !editableNow) {
      const works = input.factPath?.startsWith(WORKS_PREFIX)
      return <div className="fin-adj-field" key={input.key}>
        <div className="fin-adj-h"><span className="fld-label">{label}</span><b className="fin-adj-v">{formatInputValue(input, resolved.value)}</b></div>
        {works ? <button type="button" className="fin-link fin-adj-link" onClick={showRefurbishmentPanel}>Change it in Refurbishment</button> : help ? <p className="fin-faint">{help}</p> : null}
      </div>
    }
    const kind = editKind(input)
    const draft = input.key in drafts ? drafts[input.key] ?? null : tokenToDraft(input, resolved.value)
    const outcome = input.key in drafts ? draftToOverride(input, draft) : null
    const error = outcome && !outcome.ok ? outcome.message : undefined
    const overridden = resolved.source === 'override'
    const emptied = Boolean(outcome && outcome.ok && 'clear' in outcome)
    const pendingRemoval = Boolean(input.propertyOverrideKey && (removed[input.propertyOverrideKey] || (emptied && hasSaved(input.propertyOverrideKey))))
    const extra = <>
      {overridden && !(input.key in drafts) ? <Button small onClick={() => clearOverride(input)}>Use the model value</Button> : null}
      {pendingRemoval ? <p className="fin-faint">Your override is cleared when you apply.</p> : emptied ? <p className="fin-faint">Left empty, the model value is used.</p> : null}
    </>
    if (kind === 'toggle') {
      return <div className="fin-adj-field" key={input.key}>
        <Switch id={id} checked={draft === true} onChange={checked => stage(input, checked)} label={input.label} note={help ?? undefined} />
        <div className="form-row"><SourceChip source={resolved.source} />{extra}</div>
      </div>
    }
    if (kind === 'select') {
      const options = (input.semanticType.enumValues ?? []).map(value => ({ value, label: settingsOptionLabel(value) }))
      return <div className="fin-adj-field" key={input.key}>
        <Field label={label} hint={help ?? undefined} error={error} htmlFor={id}>
          <Select id={id} value={typeof draft === 'string' ? draft : null} options={options} onChange={next => stage(input, next)} placeholder={input.semanticType.nullable ? 'Not set' : undefined} />
        </Field>
        {extra}
      </div>
    }
    const prefix = kind === 'pence' || kind === 'money' ? '£' : undefined
    const suffix = kind === 'percent' || kind === 'percentage' ? '%' : kind === 'integer' || kind === 'number' ? input.unit ?? undefined : undefined
    return <div className="fin-adj-field" key={input.key}>
      <Field label={label} hint={help ?? undefined} error={error} htmlFor={id}>
        {kind === 'text'
          ? <TextInput id={id} value={typeof draft === 'string' ? draft : ''} onChange={value => stage(input, value)} invalid={Boolean(error)} placeholder={input.control === 'date' ? 'YYYY-MM-DD' : undefined} />
          : <NumberInput id={id} value={typeof draft === 'string' ? draft : ''} onChange={value => stage(input, value)} invalid={Boolean(error)} prefix={prefix} suffix={suffix} decimals={kind !== 'integer'} />}
      </Field>
      {extra}
    </div>
  }

  const conflict = save.error instanceof ApiError && save.error.status === 409
  return <section className="panel fin-adj" aria-labelledby={`${baseId}-title`}>
    <div className="panel-h"><span className="t-section" id={`${baseId}-title`} ref={headingRef} tabIndex={-1}>Adjust</span></div>
    <p className="fin-faint">Results are recalculated when you save a change. Values here apply to this property only.</p>
    {overrides.isError ? <p className="fin-faint">Your saved values for this property are unavailable ({overrides.error?.message}). Model values are shown and cannot be changed here.</p> : null}
    {saved?.storedSourceSha256 && saved.storedSourceSha256 !== saved.sourceSha256 ? <Alert warn tight title="Model changed">Your saved values were made for an earlier version of this model. Apply or Reset to use them with the current version.</Alert> : null}
    {groups.primary.length ? <div className="fin-adj-fields">{groups.primary.map(field)}</div> : <p className="fin-faint">This model has no main inputs to adjust.</p>}
    {save.isError ? <Alert tight title={saveFailureTitle(save.error, 'Values could not be saved')} action={conflict ? <Button small onClick={() => void reload()}>Reload values</Button> : undefined}>
      {save.error.message}
      {isFinancialSyncBusy(save.error) ? ' Nothing was saved; you can apply again.' : null}
      {save.error instanceof ApiError && save.error.status === 400 && saved?.storedSourceSha256 && saved.storedSourceSha256 !== saved.sourceSha256 ? ' Reset clears values the current model no longer accepts.' : null}
    </Alert> : null}
    {saved ? <div className="fin-adj-acts">
      <p className="fin-faint fin-adj-note" role="status">{notice}</p>
      <Button small onClick={reset} disabled={!ready || (!savedCount && !pending)} title="Clear every value you saved for this property">Reset</Button>
      <Button small kind="primary" onClick={apply} busy={save.isPending} disabled={!ready || invalid || !pending}>Apply{pending ? ` (${pending})` : ''}</Button>
    </div> : null}
    {groups.assumption.length ? <details className="fin-disclosure is-quiet"><summary>Assumptions ({groups.assumption.length})</summary><ReadOnlyList inputs={groups.assumption} context={context} /></details> : null}
    {groups.target.length ? <details className="fin-disclosure is-quiet"><summary>{hasReturnTargets ? 'Model target defaults' : 'Targets'} ({groups.target.length})</summary>{hasReturnTargets ? <p className="fin-faint">Your target replaces the model return requirement. Other deal constraints still apply; Reset restores these model return defaults.</p> : null}<ReadOnlyList inputs={groups.target} context={context} /></details> : null}
    {modelDataLink ? <div className="form-row">{modelDataLink}</div> : null}
  </section>
}
